import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { cn } from '../../../lib/utils';
import { Badge } from '../../ui/Badge';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/Popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../ui/Tooltip';
import { ACCENT_CLASS, BADGE_LABEL } from './constants';
import { loadGroupExpansion, saveGroupExpansion } from './groupExpansion';
import { SidebarSubItem } from './SidebarSubItem';
import type { SidebarChildItem, SidebarGroupItem } from './index';

type Props = {
  item: SidebarGroupItem;
  collapsed: boolean;
  onNavigate?: () => void;
};

function hasActiveChild(children: SidebarChildItem[], pathname: string): boolean {
  return children.some(
    (c) => c.path != null && (pathname === c.path || pathname.startsWith(`${c.path}/`)),
  );
}

function GroupIcon({
  icon,
  accent,
  active,
}: {
  icon?: ReactNode;
  accent?: SidebarGroupItem['accent'];
  active: boolean;
}) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex h-4 w-4 shrink-0 items-center justify-center [&_svg]:size-4',
        active ? undefined : accent ? ACCENT_CLASS[accent] : undefined,
      )}
    >
      {icon}
    </span>
  );
}

function Submenu({
  items,
  rail,
  onNavigate,
}: {
  items: SidebarChildItem[];
  rail: boolean;
  onNavigate?: () => void;
}) {
  const list = (
    <ul className="flex min-w-0 flex-1 flex-col">
      {items.map((c) => (
        <li key={c.path ?? c.label} className="w-full">
          <SidebarSubItem
            to={c.path}
            label={c.label}
            end={c.end}
            badge={c.badge}
            disabled={c.disabled}
            onNavigate={onNavigate}
          />
        </li>
      ))}
    </ul>
  );

  // Inline submenu draws the connector rail (a hairline under the group icon);
  // the flyout lists items flush (no rail).
  if (!rail) return list;
  return (
    <div className="relative flex">
      <div aria-hidden="true" className="absolute bottom-1 left-4 top-0 w-px bg-sidebar-border" />
      <div className="ml-6 flex min-w-0 flex-1">{list}</div>
    </div>
  );
}

export function SidebarGroup({ item, collapsed, onNavigate }: Props) {
  const { label, icon, accent, badge, children } = item;
  const { pathname } = useLocation();
  const active = hasActiveChild(children, pathname);

  // Groups default to open (every item visible on an expanded rail); a user's
  // explicit collapse persists via localStorage.
  const [expanded, setExpanded] = useState<boolean>(() => loadGroupExpansion()[label] ?? true);
  // Auto-open the group that owns the active route (e.g. after a deep link).
  useEffect(() => {
    if (active) setExpanded(true);
  }, [active]);

  const toggle = () => {
    setExpanded((prev) => {
      const next = !prev;
      saveGroupExpansion(label, next);
      return next;
    });
  };

  // Collapsed rail: the group is an icon that opens its submenu in a flyout;
  // hover surfaces the label as a tooltip, same as icon-only leaf rows.
  if (collapsed) {
    return (
      <Popover>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label={label}
                data-testid="nav-group-trigger"
                className={cn(
                  'flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring',
                  active
                    ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                    : 'text-sidebar-foreground hover:bg-sidebar-neutral-hover',
                )}
              >
                <GroupIcon icon={icon} accent={accent} active={active} />
              </button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="right">{label}</TooltipContent>
        </Tooltip>
        <PopoverContent
          side="right"
          align="start"
          className="w-auto min-w-[192px] rounded-[16px] border-sidebar-border bg-sidebar p-2 text-sidebar-foreground shadow-[0_4px_16px_rgba(0,0,0,0.35)]"
        >
          <div className="flex h-8 items-center gap-2 px-2">
            <GroupIcon icon={icon} accent={accent} active={false} />
            <span className="flex-1 truncate text-left text-sm text-sidebar-muted">{label}</span>
          </div>
          <Submenu items={children} rail={false} onNavigate={onNavigate} />
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={expanded}
        data-testid="nav-group-header"
        className={cn(
          'flex h-8 w-full cursor-pointer items-center gap-2 rounded-md pl-2 pr-3 text-sm transition-colors',
          'hover:bg-sidebar-neutral-hover',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring',
        )}
      >
        <GroupIcon icon={icon} accent={accent} active={false} />
        <span
          className={cn(
            'flex-1 truncate text-left',
            expanded ? 'text-sidebar-muted' : 'text-sidebar-foreground',
          )}
        >
          {label}
        </span>
        {badge && (
          <Badge variant={badge} className="ml-1 shrink-0">
            {BADGE_LABEL[badge]}
          </Badge>
        )}
      </button>
      {expanded && <Submenu items={children} rail onNavigate={onNavigate} />}
    </div>
  );
}
